import _slicedToArray from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread";
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { readConfObject } from '@jbrowse/core/configuration';
import { bpToPx, measureText } from '@jbrowse/core/util';
import { SceneGraph } from '@jbrowse/core/util/layouts';
// locals
import FeatureGlyph from './FeatureGlyph';
import SvgOverlay from './SvgOverlay';
import { chooseGlyphComponent, layOut } from './util';
// used to make features have a little padding for their labels
var xPadding = 3;
var yPadding = 5;
// used so that user can click-away-from-feature below the laid out features
// (issue #1248)
var svgHeightPadding = 100;
function RenderedFeatureGlyph(props) {
  var feature = props.feature,
    detectRerender = props.detectRerender,
    bpPerPx = props.bpPerPx,
    region = props.region,
    config = props.config,
    displayMode = props.displayMode,
    layout = props.layout,
    extraGlyphs = props.extraGlyphs; // used for unit testing, difficult to mock out so it is in actual source code
  detectRerender === null || detectRerender === void 0 ? void 0 : detectRerender();
  var reversed = region.reversed;
  var start = feature.get(reversed ? 'end' : 'start');
  var startPx = bpToPx(start, region, bpPerPx);
  var labelAllowed = displayMode !== 'collapsed';
  var rootLayout = new SceneGraph('root', 0, 0, 0, 0);
  var GlyphComponent = chooseGlyphComponent(feature, extraGlyphs);
  var featureLayout = (GlyphComponent.layOut || layOut)({
    layout: rootLayout,
    feature: feature,
    bpPerPx: bpPerPx,
    reversed: reversed,
    config: config,
    extraGlyphs: extraGlyphs
  });
  var shouldShowName = false;
  var shouldShowDescription = false;
  var name = '';
  var description = '';
  var fontHeight = 0;
  var expansion = 0;
  if (labelAllowed) {
    var showLabels = readConfObject(config, 'showLabels');
    var showDescriptions = readConfObject(config, 'showDescriptions');
    fontHeight = readConfObject(config, ['labels', 'fontSize'], {
      feature: feature
    });
    expansion = readConfObject(config, 'maxFeatureGlyphExpansion') || 0;
    name = String(readConfObject(config, ['labels', 'name'], {
      feature: feature
    }) || '');
    shouldShowName = /\S/.test(name) && showLabels;
    var getWidth = function getWidth(text) {
      var glyphWidth = rootLayout.width + expansion;
      var textWidth = measureText(text, fontHeight);
      return Math.round(Math.min(textWidth, glyphWidth));
    };
    description = String(readConfObject(config, ['labels', 'description'], {
      feature: feature
    }) || '');
    shouldShowDescription = /\S/.test(description) && showDescriptions;
    if (shouldShowName) {
      rootLayout.addChild('nameLabel', 0, featureLayout.bottom, getWidth(name), fontHeight);
    }
    if (shouldShowDescription) {
      var aboveLayout = shouldShowName ? rootLayout.getSubRecord('nameLabel') : featureLayout;
      rootLayout.addChild('descriptionLabel', 0, aboveLayout.bottom, getWidth(description), fontHeight);
    }
  }
  var topPx = layout.addRect(feature.id(), feature.get('start'), feature.get('start') + rootLayout.width * bpPerPx + xPadding * bpPerPx, rootLayout.height + yPadding);
  if (topPx === null) {
    return null;
  }
  rootLayout.move(startPx, topPx);
  return React.createElement(FeatureGlyph, _objectSpread({
    rootLayout: rootLayout,
    name: name,
    shouldShowName: shouldShowName,
    description: description,
    shouldShowDescription: shouldShowDescription,
    fontHeight: fontHeight,
    allowedWidthExpansion: expansion,
    reversed: region.reversed,
    topLevel: true
  }, props));
}
var RenderedFeatures = observer(function (props) {
  var _props$features = props.features,
    features = _props$features === void 0 ? new Map() : _props$features,
    isFeatureDisplayed = props.isFeatureDisplayed;
  return React.createElement(React.Fragment, null, _toConsumableArray(features.values()).filter(function (feature) {
    return isFeatureDisplayed ? isFeatureDisplayed(feature) : true;
  }).map(function (feature) {
    return React.createElement(RenderedFeatureGlyph, _objectSpread({
      key: feature.id(),
      feature: feature
    }, props));
  }));
});
function SvgFeatureRendering(props) {
  var layout = props.layout,
    blockKey = props.blockKey,
    regions = props.regions,
    bpPerPx = props.bpPerPx,
    config = props.config,
    _props$displayModel = props.displayModel,
    displayModel = _props$displayModel === void 0 ? {} : _props$displayModel,
    exportSVG = props.exportSVG,
    featureDisplayHandler = props.featureDisplayHandler,
    onMouseOut = props.onMouseOut,
    onMouseDown = props.onMouseDown,
    onMouseLeave = props.onMouseLeave,
    onMouseEnter = props.onMouseEnter,
    onMouseOver = props.onMouseOver,
    onMouseMove = props.onMouseMove,
    onMouseUp = props.onMouseUp,
    onClick = props.onClick;
  var _ref = regions || [],
    _ref2 = _slicedToArray(_ref, 1),
    region = _ref2[0];
  var width = (region.end - region.start) / bpPerPx;
  var displayMode = readConfObject(config, 'displayMode');
  var ref = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    mouseIsDown = _useState2[0],
    setMouseIsDown = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    height = _useState4[0],
    setHeight = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    movedDuringLastMouseDown = _useState6[0],
    setMovedDuringLastMouseDown = _useState6[1];
  var mouseDown = useCallback(function (event) {
    setMouseIsDown(true);
    setMovedDuringLastMouseDown(false);
    return onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown(event);
  }, [onMouseDown]);
  var mouseUp = useCallback(function (event) {
    setMouseIsDown(false);
    return onMouseUp === null || onMouseUp === void 0 ? void 0 : onMouseUp(event);
  }, [onMouseUp]);
  var mouseMove = useCallback(function (event) {
    var _a;
    if (!ref.current) {
      return;
    }
    if (mouseIsDown) {
      setMovedDuringLastMouseDown(true);
    }
    var _ref$current$getBound = ref.current.getBoundingClientRect(),
      left = _ref$current$getBound.left,
      top = _ref$current$getBound.top;
    var offsetX = event.clientX - left;
    var offsetY = event.clientY - top;
    var px = region.reversed ? width - offsetX : offsetX;
    var clientBp = region.start + bpPerPx * px;
    var featureIdCurrentlyUnderMouse = (_a = displayModel.getFeatureOverlapping) === null || _a === void 0 ? void 0 : _a.call(displayModel, blockKey, clientBp, offsetY);
    if (onMouseMove) {
      onMouseMove(event, featureIdCurrentlyUnderMouse);
    }
  }, [blockKey, bpPerPx, mouseIsDown, onMouseMove, region.reversed, region.start, displayModel, width]);
  var click = useCallback(function (event) {
    // don't select a feature if we are clicking and dragging
    if (movedDuringLastMouseDown) {
      return;
    }
    onClick === null || onClick === void 0 ? void 0 : onClick(event);
  }, [movedDuringLastMouseDown, onClick]);
  useEffect(function () {
    setHeight(layout.getTotalHeight());
  }, [layout]);
  if (exportSVG) {
    return React.createElement(RenderedFeatures, _objectSpread({
      displayMode: displayMode,
      isFeatureDisplayed: featureDisplayHandler,
      region: region
    }, props));
  }
  return React.createElement("svg", {
    ref: ref,
    "data-testid": "svgfeatures",
    width: width,
    height: height + svgHeightPadding,
    onMouseDown: mouseDown,
    onMouseUp: mouseUp,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut,
    onMouseMove: mouseMove,
    onClick: click
  }, React.createElement(RenderedFeatures, _objectSpread({
    displayMode: displayMode,
    region: region,
    movedDuringLastMouseDown: movedDuringLastMouseDown,
    isFeatureDisplayed: featureDisplayHandler
  }, props)), React.createElement(SvgOverlay, _objectSpread({}, props, {
    region: region,
    movedDuringLastMouseDown: movedDuringLastMouseDown
  })));
}
export default observer(SvgFeatureRendering);